import { Cyclos, Network, Wallet } from "@cyclos-io/sdk";
import { useConnectedWallet, useSolana } from "@saberhq/use-solana";
import { useSnackbar } from "notistack";
import { createContext, useEffect, useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import { NFT_MINT } from "../constants";
import { getTokenPrice, getTokens } from "../utils";
import { SolscanToken } from "../utils/types";

interface IProps {
  children: JSX.Element[] | JSX.Element;
}

export const WalletContext = createContext({} as any);

export function WalletProvider(props: IProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { connected, disconnect, connection, walletProviderInfo } = useSolana();
  const [isTokenPresent, setIsTokenPresent] = useState(false);
  const [cysPrice, setCysPrice] = useState(0);
  const [allTokensDetailed, setAllTokensDetailed] = useState<SolscanToken[]>(
    []
  );
  const [refetch, setRefetch] = useState(false);
  const newWallet = useConnectedWallet();

  const cyclosClient: Cyclos = new Cyclos(
    connection,
    Network.MAIN,
    newWallet as Wallet
  );

  useEffect(() => {
    getTokenPrice("CYS").then((p: any) => {
      setCysPrice(p);
    });
  }, [connection]);

  useEffect(() => {
    if (newWallet?.publicKey) {
      enqueueSnackbar("Wallet connected", {
        variant: "success",
        preventDuplicate: true,
      });
    }
    newWallet?.on("disconnect", () => {
      enqueueSnackbar("Wallet disconnected", {
        variant: "info",
        preventDuplicate: true,
      });
    });
  }, [newWallet]);

  useEffect(() => {
    if (newWallet?.publicKey) {
      getTokens(newWallet?.publicKey.toString())
        .then((tokens) => {
          setAllTokensDetailed(tokens ?? []);
          setIsTokenPresent(
            tokens?.some(
              (token: SolscanToken) =>
                token.tokenAddress === NFT_MINT &&
                token.tokenAmount.uiAmount > 0
            ) ?? false
          );
        })
        .catch((e) => {
          setIsTokenPresent(false);
          setAllTokensDetailed([]);
          console.error(e);
        });
    } else {
      setIsTokenPresent(false);
      setAllTokensDetailed([]);
    }
  }, [newWallet, refetch]);

  const ICON = walletProviderInfo?.icon;
  const callRefetch = () => setRefetch((r) => !r);

  const value = {
    walletProvider: walletProviderInfo,
    wallet: newWallet as any,
    isConnected: connected,
    connection,
    cyclosClient,
    connect: newWallet?.connect,
    disconnect,
    ICON,
    cysPrice,
    allTokensDetailed,
    isTokenPresent,
    callRefetch,
  };

  return (
    <WalletContext.Provider value={value}>
      {props.children}
    </WalletContext.Provider>
  );
}
